import React, { useState } from 'react';
import { AdminCrudShell } from '@/components/admin/AdminCrudShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Ban } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'active', label: 'Activa' },
  { value: 'past_due', label: 'Atrasada' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'expired', label: 'Expirada' },
];

const COLOR = {
  active: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  pending: 'bg-amber-100 text-amber-800',
  past_due: 'bg-amber-100 text-amber-900',
  cancelled: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]',
  expired: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]',
};

const CANCELABLE = new Set(['active', 'past_due']);

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function UserCell({ row }) {
  const name = (row.user_name || '').trim();
  const email = row.user_email || '';
  if (!name && !email) return <span className="text-[var(--ns-text-muted)]">—</span>;
  if (!name) return <span className="text-xs" data-testid="sub-user-email">{email}</span>;
  return (
    <div className="flex flex-col leading-tight" data-testid="sub-user-cell">
      <span className="text-sm font-medium">{name}</span>
      <span className="text-xs text-[var(--ns-text-muted)]">{email}</span>
    </div>
  );
}

// Acción por fila: dos modos de cancelación con doble confirmación para el modo inmediato.
// Se re-exporta como componente de fila para `AdminCrudShell`.
function makeRowActions(onReload) {
  return function CancelAction({ row }) {
    const [open, setOpen] = useState(false);
    const [confirmImmediate, setConfirmImmediate] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!CANCELABLE.has(row.status)) return null;
    // Siguiendo el patrón de DashboardMonetization: si ya hay cancelación
    // programada al final del período, solo ofrecemos cancelar inmediatamente.
    const alreadyScheduled = row.cancel_at_period_end === true;

    const run = async (mode) => {
      try {
        setBusy(true);
        await api.post(`/admin/subscriptions/${encodeURIComponent(row.id)}/cancel`, { mode });
        toast.success(
          mode === 'period_end'
            ? 'Cancelación programada al final del período.'
            : 'Suscripción cancelada inmediatamente.'
        );
        setOpen(false);
        setConfirmImmediate(false);
        onReload?.();
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Error al cancelar.');
      } finally {
        setBusy(false);
      }
    };

    return (
      <>
        <Button
          size="sm" variant="ghost"
          onClick={() => setOpen(true)}
          className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid={`admin-subscriptions-cancel-${row.id}`}
          title="Cancelar suscripción"
        >
          <Ban className="h-3.5 w-3.5 mr-1" /> Cancelar
        </Button>
        <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setConfirmImmediate(false); } }}>
          <AlertDialogContent className="bg-white" data-testid="cancel-sub-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Cancelar suscripción</AlertDialogTitle>
              <AlertDialogDescription>
                {row.user_name || row.user_email || row.user_id}
                {row.plan_name ? <> · <span className="font-medium">{row.plan_name}</span></> : null}
                <br />Elige cómo cancelar esta suscripción.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {confirmImmediate ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="cancel-sub-immediate-warning">
                <strong>Confirma cancelación inmediata.</strong><br />
                El acceso se retira ahora: la suscripción pasa a <code>CANCELLED</code> y el entitlement a <code>EXPIRED</code>. Esta acción no se puede deshacer.
              </div>
            ) : (
              <div className="space-y-3 py-1">
                {!alreadyScheduled && (
                  <div className="rounded-md border border-[var(--ns-border)] bg-[var(--ns-bg)]/40 p-3 text-sm" data-testid="cancel-sub-opt-period-end">
                    <div className="font-medium text-[var(--ns-text)]">Cancelar al final del período</div>
                    <div className="text-xs text-[var(--ns-text-muted)]">Mantiene el acceso hasta la fecha ya pagada e impide futuras renovaciones.</div>
                  </div>
                )}
                {alreadyScheduled && (
                  <div className="rounded-md border border-[var(--ns-border)] bg-[var(--ns-bg-2)] p-3 text-xs text-[var(--ns-text-muted)]" data-testid="cancel-sub-already-scheduled">
                    Esta suscripción ya está programada para cancelarse al final del período. Solo puedes forzar su cancelación inmediata.
                  </div>
                )}
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm" data-testid="cancel-sub-opt-immediate">
                  <div className="font-medium text-red-900">Cancelar inmediatamente</div>
                  <div className="text-xs text-red-800">Retira el acceso ahora: estado <code>CANCELLED</code> y entitlement <code>EXPIRED</code>.</div>
                </div>
              </div>
            )}
            <AlertDialogFooter className="gap-2">
              {confirmImmediate ? (
                <>
                  <AlertDialogCancel disabled={busy} onClick={() => setConfirmImmediate(false)} data-testid="cancel-sub-back">Volver</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={busy}
                    onClick={(e) => { e.preventDefault(); run('immediate'); }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="cancel-sub-confirm-immediate"
                  >Sí, cancelar ahora</AlertDialogAction>
                </>
              ) : (
                <>
                  <AlertDialogCancel disabled={busy} data-testid="cancel-sub-close">Cerrar</AlertDialogCancel>
                  {!alreadyScheduled && (
                    <Button
                      variant="outline" disabled={busy}
                      onClick={() => run('period_end')}
                      data-testid="cancel-sub-confirm-period-end"
                    >Al final del período</Button>
                  )}
                  <Button
                    variant="destructive" disabled={busy}
                    onClick={() => setConfirmImmediate(true)}
                    data-testid="cancel-sub-request-immediate"
                  >Inmediatamente…</Button>
                </>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };
}

export default function AdminSubscriptions() {
  // Truco mínimo para forzar recarga de la tabla tras cancelar sin tocar AdminCrudShell:
  // cambiamos la key del shell para que el componente remonte y re-fetchee.
  const [reloadKey, setReloadKey] = useState(0);
  const RowActions = React.useMemo(() => makeRowActions(() => setReloadKey((k) => k + 1)), []);

  return (
    <>
      {/* Sticky right en la última columna del listado para que la acción
          Cancelar siempre quede visible, aunque haya scroll horizontal.
          Alcance limitado al listado de /admin/suscripciones mediante
          data-testid del shell, sin modificar AdminCrudShell. */}
      <style>{`
        [data-testid="admin-subscriptions-table"] { position: relative; }
        [data-testid="admin-subscriptions-table"] thead th:last-child,
        [data-testid="admin-subscriptions-table"] tbody td:last-child {
          position: sticky; right: 0; z-index: 2;
          min-width: 200px;
          box-shadow: -8px 0 10px -8px rgba(28,22,18,0.18);
        }
        [data-testid="admin-subscriptions-table"] thead th:last-child {
          background: var(--ns-bg-2);
        }
        [data-testid="admin-subscriptions-table"] tbody td:last-child {
          background: #ffffff;
        }
        [data-testid="admin-subscriptions-table"] tbody tr:hover td:last-child {
          background: color-mix(in srgb, var(--ns-bg) 40%, #ffffff);
        }
      `}</style>
      <AdminCrudShell
        key={reloadKey}
        title="Suscripciones"
        subtitle="Listado de suscripciones activas y históricas. Solo lectura."
        entity="subscriptions"
        testIdPrefix="admin-subscriptions"
        readOnly
        canDelete={false}
        searchPlaceholder="Buscar usuario…"
        rowActions={RowActions}
        filters={[{ name: 'status', label: 'Estado', options: STATUS_OPTS }]}
        columns={[
          { key: 'user', label: 'Usuario', render: (r) => <UserCell row={r} /> },
          { key: 'plan', label: 'Plan', render: (r) => <span className="text-sm">{r.plan_name || '—'}</span> },
          { key: 'provider', label: 'Proveedor', render: (r) => <Badge variant="secondary" className="uppercase tracking-wider text-[10px]">{r.provider}</Badge> },
          { key: 'status', label: 'Estado', render: (r) => {
            const isActivePending = r.status === 'active' && r.cancel_at_period_end === true;
            const label = isActivePending ? 'ACTIVA · CANCELA AL FINAL' : r.status;
            return <Badge className={`text-[10px] uppercase ${COLOR[r.status] || ''}`} data-testid="sub-status-badge">{label}</Badge>;
          },
        },
          { key: 'current_period_start', label: 'Inicio', render: (r) => <span className="text-xs whitespace-nowrap" data-testid="sub-period-start">{fmtDateTime(r.current_period_start)}</span> },
          { key: 'current_period_end', label: 'Fin', render: (r) => <span className="text-xs whitespace-nowrap" data-testid="sub-period-end">{fmtDateTime(r.current_period_end)}</span> },
          { key: 'next_renewal_at', label: 'Próxima renovación', render: (r) => <span className="text-xs whitespace-nowrap" data-testid="sub-next-renewal">{fmtDateTime(r.next_renewal_at)}</span> },
        ]}
      />
    </>
  );
}
